// src/login.jsx
import React, { useMemo, useState } from "react";
import { saveAuth } from "./auth";
import { useNavigate } from "react-router-dom";

import {
  Avatar,
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import LockOutlined from "@mui/icons-material/LockOutlined";
import Facebook from "@mui/icons-material/Facebook";
import Instagram from "@mui/icons-material/Instagram";
import GitHub from "@mui/icons-material/GitHub";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import api from "./Api";

// --- validador simple de email ---
const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(String(email || "").trim());

export default function LoginSignup({ onAuthed }) {
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  // Campos
  const [values, setValues] = useState({ nombre: "", email: "", password: "" });
  const isLogin = mode === "login";

  const subtitle = useMemo(
    () => (isLogin ? "Bienvenido otra vez" : "Crea tu cuenta"),
    [isLogin]
  );

  // Helpers
  const handleChange = (key) => (e) =>
    setValues((prev) => ({ ...prev, [key]: e.target.value }));

  const toggleMode = (newMode) => {
    if (newMode !== mode) {
      setMode(newMode);
      setErr("");
    }
  };

  const toggleShowPass = () => setShowPass((s) => !s);
  const resetError = () => setErr("");

  const trimForm = () => ({
    nombre: values.nombre.trim(),
    email: values.email.trim(),
    password: values.password, // no trim en password
  });

  // Validaciones
  const validateLogin = ({ email, password }) => {
    if (!email || !password) return "Debes ingresar email y contraseña.";
    if (!isValidEmail(email)) return "El email no es válido.";
    return "";
  };

  const validateRegister = ({ nombre, email, password }) => {
    if (!nombre || !email || !password) return "Completa todos los campos.";
    if (!isValidEmail(email)) return "El email no es válido.";
    return "";
  };

  // API
  const apiLogin = async ({ email, password }) => {
    return api.post("", { action: "login", email, password });
  };
  const apiRegister = async ({ nombre, email, password }) => {
    return api.post("", { nombre, email, password });
  };

  // Flujos
  const submitLogin = async ({ email, password }) => {
    const vErr = validateLogin({ email, password });
    if (vErr) { setErr(vErr); return; }

    const res = await apiLogin({ email, password });
    if (res.data?.success) {
      const user = res.data.user;
      const token = res.data.token; // si tu backend envía token
      saveAuth({ user, token });    // <-- persistir sesión
      onAuthed?.(user);
      navigate("/personas");        // redirección
    } else {
      setErr(res.data?.error || "Credenciales incorrectas");
    }
  };

  const submitRegister = async ({ nombre, email, password }) => {
    const vErr = validateRegister({ nombre, email, password });
    if (vErr) { setErr(vErr); return; }

    const res = await apiRegister({ nombre, email, password });
    if (res.data?.success) {
      // login automático
      const login = await apiLogin({ email, password });
      const user = login.data?.user ?? null;
      const token = login.data?.token;
      saveAuth({ user, token });    // <-- persistir sesión
      onAuthed?.(user);
      navigate("/personas");
    } else {
      setErr(res.data?.error || "No se pudo registrar");
    }
  };

  // Submit maestro
  const handleSubmit = async (e) => {
    e.preventDefault();
    resetError();
    setLoading(true);
    try {
      const data = trimForm();
      if (isLogin) {
        await submitLogin({ email: data.email, password: data.password });
      } else {
        await submitRegister({
          nombre: data.nombre,
          email: data.email,
          password: data.password,
        });
      }
    } catch {
      setErr("No se pudo conectar con el servidor");
    } finally {
      setLoading(false);
    }
  };

  // Render
  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#0f172a",
        margin: 0,
        padding: 0,
      }}
    >
      <Container maxWidth="xs" sx={{ position: "relative", zIndex: 1 }}>
        <Paper elevation={0} sx={card}>
          {/* Toggle superior tipo pastilla */}
          <Box sx={toggleWrap}>
            <Box
              sx={{ ...toggleHighlight, left: isLogin ? 6 : "calc(50% + 6px)" }}
            />
            <Button
              onClick={() => toggleMode("login")}
              sx={{ ...toggleBtn, color: isLogin ? "#0a0a0a" : "#cfd8ff" }}
            >
              Log in
            </Button>
            <Button
              onClick={() => toggleMode("register")}
              sx={{ ...toggleBtn, color: !isLogin ? "#0a0a0a" : "#cfd8ff" }}
            >
              Register
            </Button>
          </Box>

          {/* Social (decorativo) */}
          <Box sx={socialRow}>
            <IconButton sx={{ ...socialBtn, ...glow("#1877F2") }}>
              <Facebook />
            </IconButton>
            <IconButton sx={{ ...socialBtn, ...glow("#E1306C") }}>
              <Instagram />
            </IconButton>
            <IconButton sx={{ ...socialBtn, ...glow("#00D09C") }}>
              <GitHub />
            </IconButton>
          </Box>

          <Box sx={{ textAlign: "center", mb: 1 }}>
            <Avatar sx={avatar}>
              <LockOutlined />
            </Avatar>
            <Typography variant="h5" sx={title}>
              AyudaApp
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,.75)" }}>
              {subtitle}
            </Typography>
          </Box>

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            {!isLogin && (
              <TextField
                fullWidth
                label="User"
                variant="standard"
                value={values.nombre}
                onChange={handleChange("nombre")}
                InputLabelProps={{ sx: label }}
                InputProps={{ sx: underlineInput }}
                sx={{ mb: 2 }}
                required
              />
            )}

            <TextField
              fullWidth
              label="Email"
              type="email"
              variant="standard"
              value={values.email}
              onChange={handleChange("email")}
              InputLabelProps={{ sx: label }}
              InputProps={{ sx: underlineInput }}
              sx={{ mb: 2 }}
              required
            />

            <TextField
              fullWidth
              label="Password"
              type={showPass ? "text" : "password"}
              variant="standard"
              value={values.password}
              onChange={handleChange("password")}
              InputLabelProps={{ sx: label }}
              InputProps={{
                sx: underlineInput,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={toggleShowPass}>
                      {showPass ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              required
            />

            {err && <Typography sx={errorBox}>{err}</Typography>}

            <Button type="submit" fullWidth disabled={loading} sx={ctaBtn}>
              {loading ? "Procesando..." : isLogin ? "Entrar" : "Register"}
            </Button>

            <Box sx={{ mt: 1, textAlign: "center" }}>
              <Link
                onClick={() => navigate("/forgot-password")}
                underline="hover"
                sx={{ color: "#89b4ff", fontSize: 13, cursor: "pointer" }}
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

/* ---------- estilos (SIN CAMBIOS DE DISEÑO) ---------- */
const card = {
  p: 4,
  borderRadius: 4,
  bgcolor: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  backdropFilter: "blur(12px)",
  boxShadow: "0 10px 30px rgba(0,0,0,.35), inset 0 0 0 1px rgba(255,255,255,.04)",
};
const toggleWrap = {
  width: 260, height: 48, mx: "auto", mb: 3, borderRadius: 30,
  position: "relative", display: "flex", alignItems: "center",
  justifyContent: "space-between", px: "6px",
  background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.16)", overflow: "hidden",
};
const toggleHighlight = {
  position: "absolute", top: 6, width: "calc(50% - 6px)", height: "calc(100% - 12px)",
  borderRadius: 24, background: "linear-gradient(90deg, #70fbd3 0%, #6a7dff 60%, #9b59ff 100%)",
  boxShadow: "0 10px 22px rgba(105,90,255,.45)", transition: "left .25s ease", zIndex: 0,
};
const toggleBtn = { flex: 1, zIndex: 1, fontWeight: 700, textTransform: "none" };
const socialRow = { display: "flex", gap: 2, justifyContent: "center", mb: 2 };
const socialBtn = {
  width: 44, height: 44, color: "white", border: "1px solid rgba(255,255,255,.25)",
  background: "rgba(255,255,255,.06)", backdropFilter: "blur(6px)",
};
const glow = (c) => ({
  boxShadow: `0 0 0 0 ${c}33, 0 0 16px ${c}55, 0 0 30px ${c}66`,
  "&:hover": { boxShadow: `0 0 16px ${c}99, 0 0 36px ${c}aa` },
});
const avatar = {
  m: "10px auto 0", width: 58, height: 58,
  background: "linear-gradient(135deg, #6a7dff 0%, #9b59ff 50%, #21e6b6 100%)",
  boxShadow: "0 8px 22px rgba(105,90,255,.45)",
};
const title = {
  mt: 1, fontWeight: 800, letterSpacing: 0.6,
  background: "linear-gradient(90deg,#e6e8ff 0%, #d8c5ff 50%, #a5ffe7 100%)",
  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
};
const label = { color: "rgba(255,255,255,.7) !important" };
const underlineInput = {
  color: "rgba(255,255,255,.95)",
  "&:before": { borderBottom: "1px solid rgba(255,255,255,.25)" },
  "&:hover:not(.Mui-disabled):before": { borderBottom: "1px solid rgba(255,255,255,.45)" },
  "&:after": { borderBottom: "2px solid #79e9d0", boxShadow: "0 6px 18px rgba(121,233,208,.35)" },
};
const errorBox = {
  mt: 1.5, p: 1, borderRadius: 2, fontSize: 13, color: "#ffb3b3",
  bgcolor: "rgba(255,0,0,.12)", border: "1px solid rgba(255,0,0,.25)",
};
const ctaBtn = {
  mt: 3, py: 1.4, borderRadius: 999, fontWeight: 800, letterSpacing: 0.4, color: "white",
  background: "linear-gradient(90deg, #6a7dff 0%, #9b59ff 50%, #21e6b6 100%)",
  boxShadow: "0 12px 26px rgba(105,90,255,.45), 0 8px 18px rgba(33,230,182,.25)",
  textTransform: "none",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 14px 30px rgba(105,90,255,.55), 0 10px 22px rgba(33,230,182,.35)",
  },
  transition: "all .2s ease",
};
